import { SIM_NAO, type Pergunta, type RespostaDaAnamnese } from "@bookdental/schemas";
import { cn } from "../lib/cn";

// Uma pergunta da anamnese — o mesmo componente no app (equipe) e na página do paciente (web). Só
// classes Tailwind neutras e o azul da marca escrito por extenso: a web não tem os tokens do app.

const pilula = (ativa: boolean) =>
  cn(
    "min-h-10 w-full rounded-lg border px-2 py-2 text-center text-sm font-medium leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2457FF]/40",
    ativa ? "border-[#2457FF] bg-[#2457FF]/10 text-[#1D46D6]" : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
  );
const caixaDeTexto =
  "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2457FF]/40";
/** Opções em colunas iguais, no máximo 3 por linha; listas longas ficam com 2 no celular. */
const grade = (n: number) => cn("grid gap-2", n <= 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3");
// Marcar "Nenhuma/Nenhum" limpa o resto, e marcar outra coisa tira o "Nenhuma".
const EXCLUSIVAS = ["NENHUMA", "NENHUM"];

export function PerguntaDaAnamnese({
  p,
  r,
  pendente,
  desabilitada,
  onChange,
}: {
  p: Pergunta;
  r: RespostaDaAnamnese | undefined;
  pendente?: boolean;
  desabilitada?: boolean;
  onChange: (r: RespostaDaAnamnese | undefined) => void;
}) {
  const idCampo = `anamnese-${p.id}`;
  const escolher = (valor: string) => onChange(r?.valor === valor ? undefined : { ...r, valor });
  const alternar = (valor: string) => {
    const atuais = r?.valores ?? [];
    const proximos = atuais.includes(valor)
      ? atuais.filter((v) => v !== valor)
      : EXCLUSIVAS.includes(valor)
        ? [valor]
        : [...atuais.filter((v) => !EXCLUSIVAS.includes(v)), valor];
    onChange(proximos.length ? { valores: proximos } : undefined);
  };

  return (
    <fieldset disabled={desabilitada} className={cn("space-y-2.5 rounded-xl p-3", pendente && "bg-amber-50 ring-1 ring-amber-300")}>
      <legend className="sr-only">{p.texto}</legend>
      <p className="text-[15px] font-medium leading-snug text-zinc-900" id={`${idCampo}-titulo`}>
        {p.texto}
        {p.obrigatoria && <span className="ml-1 text-red-500" aria-label="obrigatória">*</span>}
      </p>
      {p.ajuda && <p className="text-xs text-zinc-500">{p.ajuda}</p>}

      {(p.tipo === "SIM_NAO" || p.tipo === "ESCOLHA") && (
        <div role="radiogroup" aria-labelledby={`${idCampo}-titulo`} className={grade(p.tipo === "SIM_NAO" ? 3 : (p.opcoes?.length ?? 0))}>
          {(p.tipo === "SIM_NAO" ? SIM_NAO : (p.opcoes ?? [])).map((o) => (
            <button key={o.valor} type="button" role="radio" aria-checked={r?.valor === o.valor} onClick={() => escolher(o.valor)} className={pilula(r?.valor === o.valor)}>
              {o.rotulo}
            </button>
          ))}
        </div>
      )}

      {p.tipo === "MULTIPLA" && (
        <div role="group" aria-labelledby={`${idCampo}-titulo`} className={grade(p.opcoes?.length ?? 0)}>
          {(p.opcoes ?? []).map((o) => {
            const ativa = !!r?.valores?.includes(o.valor);
            return (
              <button key={o.valor} type="button" aria-pressed={ativa} onClick={() => alternar(o.valor)} className={pilula(ativa)}>
                {o.rotulo}
              </button>
            );
          })}
        </div>
      )}

      {p.tipo === "ESCALA" && (
        <div role="radiogroup" aria-labelledby={`${idCampo}-titulo`} className="grid grid-cols-6 gap-1.5 sm:grid-cols-11">
          {Array.from({ length: 11 }, (_, n) => String(n)).map((n) => (
            <button key={n} type="button" role="radio" aria-checked={r?.valor === n} onClick={() => escolher(n)} className={cn(pilula(r?.valor === n), "px-0")}>
              {n}
            </button>
          ))}
        </div>
      )}

      {p.tipo === "TEXTO" && (
        <textarea
          id={idCampo}
          aria-labelledby={`${idCampo}-titulo`}
          rows={2}
          maxLength={4000}
          value={r?.valor ?? ""}
          onChange={(e) => onChange(e.target.value ? { valor: e.target.value } : undefined)}
          className={caixaDeTexto}
        />
      )}

      {p.tipo === "SIM_NAO" && p.detalhe && r?.valor === "SIM" && (
        <div className="space-y-1">
          <label htmlFor={`${idCampo}-detalhe`} className="text-sm text-zinc-700">
            {p.detalhe.rotulo}
            {p.detalhe.obrigatorio && <span className="ml-1 text-red-500">*</span>}
          </label>
          <textarea
            id={`${idCampo}-detalhe`}
            rows={2}
            maxLength={2000}
            value={r.detalhe ?? ""}
            onChange={(e) => onChange({ ...r, detalhe: e.target.value || undefined })}
            className={caixaDeTexto}
          />
        </div>
      )}
    </fieldset>
  );
}
